/**
 * GetCampaignRankingContentMostLikesUseCase
 * Loads the content (most-likes) leaderboard for a campaign and remaps to
 * entities, branching live-vs-ended on `campaign.active`.
 *
 * Flutter source of truth:
 * - lib/data/repository/campaigns_repository_impl.dart:23-31 —
 *   getCampaignRankingContentMostLikes(campaign): if `campaign.active` →
 *   getCampaignContentMostLikes(campaign.id, campaign.rankingFetchLimit); else →
 *   getEndedCampaignContentMostLikes(campaign.id, campaign.rankingFetchLimit).
 * - lib/presentation/bottom_sheet/campaign_ranking_bottom_sheet.dart:119-127 —
 *   CampaignType.contentMostLikes branch calls this repository method.
 *
 * Source-shape selection (Task 3 remappers): the live `media` collection-group
 * query yields the "from media" doc shape
 * (CampaignRankingContentRemapper.fromMediaJson); the frozen `ranking`
 * subcollection yields the ranking doc shape (fromRankingJson).
 */

import { CampaignsService } from '@/domain/services/campaign/campaignsService';
import { CampaignRankingContentRemapper } from '@/domain/remappers/campaign/campaignRankingContentRemapper';
import type { CampaignEntity } from '@/domain/entities/campaign/campaignEntity';
import type { CampaignRankingContentEntity } from '@/domain/entities/campaign/campaignRankingContentEntity';

export class GetCampaignRankingContentMostLikesUseCase {
  private campaignsService: CampaignsService;

  constructor() {
    this.campaignsService = CampaignsService.getInstance();
  }

  async execute(campaign: CampaignEntity): Promise<CampaignRankingContentEntity[]> {
    if (campaign.active) {
      const items = await this.campaignsService.getCampaignContentMostLikes(
        campaign.id,
        campaign.rankingFetchLimit,
      );
      return CampaignRankingContentRemapper.fromMediaJsonList(items).map(
        CampaignRankingContentRemapper.fromDto,
      );
    }

    const items = await this.campaignsService.getEndedCampaignContentMostLikes(
      campaign.id,
      campaign.rankingFetchLimit,
    );
    return CampaignRankingContentRemapper.fromRankingJsonList(items).map(
      CampaignRankingContentRemapper.fromDto,
    );
  }
}

export const createGetCampaignRankingContentMostLikesUseCase = () =>
  new GetCampaignRankingContentMostLikesUseCase();
